import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI, systemAPI, settingsAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import toast from 'react-hot-toast';
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineUser } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { IOSButton } from '../components/ios';

function MobileRegister() {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState('');
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await systemAPI.getVersion();
        setVersion(response.data.version);
      } catch (error) {
        setVersion('1.4.1');
      }
    };

    const fetchRegistrationStatus = async () => {
      try {
        const response = await settingsAPI.getRegistrationStatus();
        setRegistrationEnabled(response.data.enabled);
      } catch (error) {
        setRegistrationEnabled(true);
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchVersion();
    fetchRegistrationStatus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || !email || !password || !confirmPassword) {
      toast.error(t('auth.fillAllFields'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('auth.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDontMatch'));
      return;
    }

    setLoading(true);
    try {
      const currentLanguage = i18next.language || 'pt-BR';
      await authAPI.register(username, email, password, currentLanguage);

      toast.success(t('auth.registerSuccess'));
      navigate('/verify-email', { state: { email } });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      setPassword('');
      setConfirmPassword('');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="mobile-auth-page">
        <div className="mobile-auth-loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (!registrationEnabled) {
    return (
      <div className="mobile-auth-page">
        <div className="mobile-auth-header">
          <h1>My Series</h1>
          <p>{t('auth.registrationDisabled')}</p>
        </div>

        <div className="mobile-auth-form">
          <Link to="/login" className="mobile-auth-submit">
            <IOSButton variant="primary" fullWidth>
              {t('auth.backToLogin')}
            </IOSButton>
          </Link>
        </div>

        <div className="mobile-auth-version">
          <p>{t('auth.copyright')}</p>
          <p>{t('common.version')} {version}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-auth-page">
      {/* Header */}
      <div className="mobile-auth-header">
        <h1>My Series</h1>
        <p>{t('auth.registerSubtitle')}</p>
      </div>

      {/* Formulário */}
      <form className="mobile-auth-form" onSubmit={handleSubmit}>
        <div className="mobile-auth-field">
          <label>{t('common.username')}</label>
          <div className="mobile-auth-input-wrapper">
            <HiOutlineUser className="mobile-auth-icon" />
            <input
              type="text"
              placeholder={t('auth.enterUsername')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              autoFocus
            />
          </div>
        </div>

        <div className="mobile-auth-field">
          <label>{t('common.email')}</label>
          <div className="mobile-auth-input-wrapper">
            <HiOutlineMail className="mobile-auth-icon" />
            <input
              type="email"
              placeholder={t('profile.enterEmail')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="mobile-auth-field">
          <label>{t('common.password')}</label>
          <div className="mobile-auth-input-wrapper">
            <HiOutlineLockClosed className="mobile-auth-icon" />
            <input
              type="password"
              placeholder={t('common.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="mobile-auth-field">
          <label>{t('auth.confirmNewPasswordLabel')}</label>
          <div className="mobile-auth-input-wrapper">
            <HiOutlineLockClosed className="mobile-auth-icon" />
            <input
              type="password"
              placeholder={t('auth.confirmNewPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <IOSButton
          type="submit"
          variant="primary"
          fullWidth
          disabled={loading}
          className="mobile-auth-submit"
        >
          {loading ? t('auth.registering') : t('auth.register')}
        </IOSButton>

        <div className="mobile-auth-footer">
          <span>{t('auth.alreadyHaveAccount')}</span>
          <Link to="/login" className="mobile-auth-link-primary">
            {t('auth.login')}
          </Link>
        </div>
      </form>

      {/* Versão */}
      <div className="mobile-auth-version">
        <p>{t('auth.copyright')}</p>
        <p>{t('common.version')} {version}</p>
      </div>
    </div>
  );
}

export default MobileRegister;
